import {
  Text,
  withDatasourceCheck,
  NextImage,
  TextField,
  LinkField,
  ImageField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import useHelpers from 'src/hooks/helpers';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Fields = {
  title: TextField;
  description: TextField;
  subtitle: TextField;
  cta: LinkField;
  image: ImageField;
};

type FeatureBannerProps = ComponentProps & {
  fields: Fields;
  params: object;
  rendering: object;
};

const FeatureBanner = ({ fields, params, rendering }: FeatureBannerProps): JSX.Element => {
  const linkDescLengthTooLong: boolean = (fields?.cta?.value?.text?.length ?? 0) > 30;
  const buttonType: string = linkDescLengthTooLong ? 'textLink' : 'primary';
  const { anchorId } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor: boolean | undefined = sitecoreContext.pageEditing;

  const isValueExists = (field: LinkField) => {
    return isExperienceEditor || field?.value?.text || field?.value?.href;
  };

  const FeatureBannerContent = () => {
    return (
      <>
        {fields.title && <Text tag="h2" field={fields.title} />}
        {fields.subtitle && <Text tag="p" field={fields.subtitle} className="subheader-1 mt-3" />}
        {fields.description && (
          <Text tag="p" field={fields.description} className="body-large mt-2" />
        )}
        {isValueExists(fields.cta) && (
          <AbrdnButton
            buttonType={buttonType}
            linkField={fields.cta}
            className="mt-5 background-to-change"
          />
        )}
      </>
    );
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering.uid}
      className={`feature-banner-section overflow-hidden pb-5 py-md-6 py-lg-0 mb-5 mb-lg-8 ${params.background}`}
    >
      <div className="container">
        <div
          className={`feature-banner row d-flex gx-lg-6 ${
            params.imagePosition === 'right' ? 'feature-banner--right-img' : ''
          }`}
        >
          <div className="feature-banner__image-column col-md-6">
            <div className="feature-banner__image">
              <NextImage field={fields.image} width="550" height="380" />
            </div>
          </div>
          <div className="col-md-6 align-self-lg-center py-lg-9">
            <div className="feature-banner__content mt-4 mt-md-0 background-to-change">
              <FeatureBannerContent />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()<FeatureBannerProps>(FeatureBanner);
